import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/apiClient";
import { Disclaimer } from "../components/Disclaimer";
import { Button } from "../components/ui/button";
import { ProposeNormaDialog } from "../components/ProposeNormaDialog";
import { useAuth } from "../lib/AuthContext";

const SEO_TITLE = "Normativa Contratación Pública Colombia — JustSapiens Procure";
const SEO_DESCRIPTION = "Lista blanca de 35 normas colombianas vigentes que sustentan los diagnósticos precontractuales de JustSapiens Procure. Constitución, Ley 80, decretos.";

// Orden visual de categorías
const CATEGORY_ORDER = [
  "Constitucional",
  "Estatuto General",
  "Decretos Reglamentarios",
  "Procedimiento",
  "Anticorrupción",
  "Protección de Datos",
  "Presupuestal",
  "Regímenes Especiales",
];

function setSEO(items) {
  document.title = SEO_TITLE;

  // meta description
  let meta = document.querySelector('meta[name="description"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", "description");
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", SEO_DESCRIPTION);

  // JSON-LD ItemList
  const existing = document.getElementById("procure-normativa-jsonld");
  if (existing) existing.remove();
  const ld = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Normativa aplicable a la contratación pública en Colombia",
    description: SEO_DESCRIPTION,
    numberOfItems: items.length,
    itemListElement: items.map((n, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      item: {
        "@type": "Legislation",
        name: n.alias,
        legislationType: n.tipo,
        legislationIdentifier: `${n.tipo} ${n.numero} de ${n.anio}`,
        about: n.tema,
        description: n.explainer,
      },
    })),
  };
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.id = "procure-normativa-jsonld";
  script.text = JSON.stringify(ld);
  document.head.appendChild(script);
}

function restoreDefaultSEO() {
  document.title = "JustSapiens Procure";
  const ld = document.getElementById("procure-normativa-jsonld");
  if (ld) ld.remove();
}

export default function NormativaPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [proposeOpen, setProposeOpen] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/public/normativa");
        if (!alive) return;
        setItems(data.items || []);
        setSEO(data.items || []);
      } catch (e) {
        if (alive) setError(formatApiError(e?.response?.data?.detail) || "No se pudo cargar la normativa");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
      restoreDefaultSEO();
    };
  }, []);

  // Agrupar por categoría manteniendo orden
  const grouped = items.reduce((acc, n) => {
    const cat = n.categoria || "Otros";
    (acc[cat] = acc[cat] || []).push(n);
    return acc;
  }, {});
  const orderedCategories = [
    ...CATEGORY_ORDER.filter((c) => grouped[c]),
    ...Object.keys(grouped).filter((c) => !CATEGORY_ORDER.includes(c)),
  ];

  return (
    <article data-testid="normativa-page" className="bg-white">
      <header className="border-b border-gray-200">
        <div className="container-prose py-16 max-w-4xl">
          <p className="text-xs uppercase tracking-[0.22em] text-primary font-medium mb-3">
            Lista blanca verificada
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl text-gray-900 leading-[1.1] mb-4">
            Normativa aplicable a la contratación pública en Colombia
          </h1>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-3xl">
            Lista blanca de normas vigentes que sustentan los diagnósticos generados por
            JustSapiens Procure. Verificadas por nuestro equipo legal. {items.length} referencias entre Constitución, leyes y decretos reglamentarios.
          </p>
        </div>
      </header>

      {loading && (
        <div className="container-prose py-12 text-sm text-gray-500">Cargando normativa…</div>
      )}

      {error && (
        <div className="container-prose py-12">
          <div className="border border-destructive/30 bg-destructive/5 text-destructive p-4 text-sm">
            {error}
          </div>
        </div>
      )}

      {!loading && !error && (
        <div className="container-prose py-16 max-w-5xl">
          {orderedCategories.map((cat) => (
            <section
              key={cat}
              data-testid={`normativa-category-${cat.toLowerCase().replace(/\s+/g, "-")}`}
              className="mb-14"
            >
              <h2 className="font-serif text-2xl text-gray-900 mb-1">{cat}</h2>
              <p className="text-xs uppercase tracking-widest text-gray-500 mb-6">
                {grouped[cat].length} referencias
              </p>
              <div className="border border-gray-200 bg-white divide-y divide-gray-200">
                {grouped[cat].map((n) => (
                  <article
                    key={`${n.tipo}-${n.numero}-${n.anio}`}
                    data-testid={`norm-${n.tipo}-${n.numero}-${n.anio}`}
                    className="grid grid-cols-1 md:grid-cols-12 gap-4 px-5 py-5"
                  >
                    <div className="md:col-span-3">
                      <h3 className="font-serif text-base text-gray-900">{n.alias}</h3>
                      <p className="text-[10px] uppercase tracking-widest text-primary mt-1">{n.tipo}</p>
                    </div>
                    <div className="md:col-span-3 text-sm text-gray-700">{n.tema}</div>
                    <div className="md:col-span-6 text-sm text-gray-600 leading-relaxed">
                      {n.explainer}
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* CTA */}
      <section className="border-t border-gray-200 bg-gray-50">
        <div className="container-prose py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="max-w-xl">
            <h2 className="font-serif text-2xl sm:text-3xl text-gray-900 mb-2">
              ¿Tu caso aplica alguna de estas normas?
            </h2>
            <p className="text-gray-600">
              Crea una cuenta gratis y registra tu caso. La prevalidación abrirá pronto y podrás contrastar tu proceso con la lista blanca completa.
            </p>
          </div>
          <Button asChild size="lg" className="rounded-sm h-12 px-7" data-testid="normativa-cta-register">
            <Link to="/register">Prevalidar mi caso</Link>
          </Button>
        </div>
      </section>

      {/* Propose a norm */}
      <section className="border-t border-gray-200">
        <div className="container-prose py-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="max-w-xl">
            <h2 className="font-serif text-xl text-gray-900 mb-1">
              ¿Falta una norma aplicable?
            </h2>
            <p className="text-sm text-gray-600">
              Propón una norma. Nuestro equipo la revisa antes de incorporarla.
            </p>
          </div>
          {user ? (
            <Button
              variant="outline"
              className="rounded-sm h-11 px-5"
              onClick={() => setProposeOpen(true)}
              data-testid="btn-propose-norma"
            >
              Proponer norma
            </Button>
          ) : (
            <Button
              asChild
              variant="outline"
              className="rounded-sm h-11 px-5"
              data-testid="btn-propose-norma-login"
            >
              <Link to="/login?next=/normativa-aplicable">Inicia sesión para proponer</Link>
            </Button>
          )}
        </div>
      </section>

      <ProposeNormaDialog open={proposeOpen} onOpenChange={setProposeOpen} />

      <section className="border-t border-gray-200">
        <div className="container-prose py-12">
          <Disclaimer dataTestId="normativa-disclaimer" />
        </div>
      </section>
    </article>
  );
}
